import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import { cn } from '@/theme/utils'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

import { BecomeValidatorAmount } from './BecomeValidatorAmount'
import { BecomeValidatorShards } from './BecomeValidatorShards'

type Step = 1 | 2

function BecomeValidatorStepper({
  step,
  mode,
}: {
  step: Step
  mode?: 'shards' | 'amount'
}) {
  const step1State = step === 1 ? 'active' : 'complete'
  const step2State = step === 2 ? 'active' : 'inactive'

  return (
    <div className='flex items-center gap-2 text-xs'>
      <div
        className={cn(
          'inline-flex flex-col items-center gap-2',
          step1State === 'active' ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        <span
          className={cn(
            'grid h-10 w-10 place-items-center rounded-full border text-[11px] font-semibold',
            step1State === 'active'
              ? 'border-primary/40 bg-primary/10'
              : 'border-primary/40 bg-primary/10',
          )}
        >
          1
        </span>
        <span>{mode === 'amount' ? 'Enter Amount' : 'Select Shards'}</span>
      </div>

      <div className='bg-border h-px flex-1' />

      <div
        className={cn(
          'inline-flex flex-col items-center gap-2',
          step2State === 'active' ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        <span
          className={cn(
            'grid h-10 w-10 place-items-center rounded-full border text-[11px] font-semibold',
            step2State === 'active'
              ? 'border-primary/40 bg-primary/10'
              : 'border-border bg-transparent',
          )}
        >
          2
        </span>
        <span>Details</span>
      </div>
    </div>
  )
}

export default function BecomeValidatorModal({
  open,
  onClose,
  selectedAddresses,
  onToggleAddress,
  mode,
  onBackToFlow,
  onSuccess,
}: {
  open: boolean
  onClose: () => void
  selectedAddresses: string[]
  onToggleAddress: (addr: string) => void
  mode?: 'shards' | 'amount'
  onBackToFlow?: () => void
  onSuccess?: () => void
}) {
  const renderTitle = (step: Step) => (
    <div className='mr-4 space-y-1'>
      <div className='text-foreground mb-8'>Become a Validator</div>
      <BecomeValidatorStepper step={step} mode={mode} />
    </div>
  )

  const renderFooter = (
    step: Step,
    opts: {
      canProceed: boolean
      isSubmitting: boolean
      onNext: () => void
      onBack?: () => void
    },
  ) => (
    <div className='mt-6 flex w-full gap-2 pr-4'>
      {step === 1 ? (
        <>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={() => {
              if (onBackToFlow) onBackToFlow()
              else onClose()
            }}
          >
            Close
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={!opts.canProceed}
            onClick={opts.onNext}
          >
            Next
          </UiButton>
        </>
      ) : (
        <>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={opts.onBack}
            disabled={opts.isSubmitting}
          >
            Back
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            type='submit'
            form='become-validator-form'
            disabled={opts.isSubmitting}
          >
            {opts.isSubmitting ? (
              <span className='inline-flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Submitting...
              </span>
            ) : (
              'Submit'
            )}
          </UiButton>
        </>
      )}
    </div>
  )

  const content =
    mode === 'amount' ? (
      <BecomeValidatorAmount
        onClose={onClose}
        onSuccess={onSuccess}
        renderTitle={renderTitle}
        renderFooter={renderFooter}
      />
    ) : (
      <BecomeValidatorShards
        selectedAddresses={selectedAddresses}
        onToggleAddress={onToggleAddress}
        onClose={onClose}
        onSuccess={onSuccess}
        renderTitle={renderTitle}
        renderFooter={renderFooter}
      />
    )

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) onClose()
      }}
      title={null}
      content={content}
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-2xl' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}
